import { SalesPerformanceAggregation, StoreSalesPerformanceResponse } from '../../../../../hooks';
import { getIndex, salesPerformancePeriods, SalesPerformancePeriod, sumRows } from '../sales.util';

export type PotentialItem = {
  id: string;
  description?: string;
  vsLY: number;
  idxArea: number;
  potentialGain: number;
};

type PotentialData = {
  index: PotentialItem[];
  subIndex: PotentialItem[];
  garmentGroup: PotentialItem[];
  department: PotentialItem[];
};

export const POTENTIAL_SECTION_MIN_WIDTH = 60;
export const POTENTIAL_CONTAINER_GAP = 8;

type TimePeriodToPotentialDataMap = Record<SalesPerformancePeriod, PotentialData>;

// function to group by index, subindex, garmentGroup, or departmentNumber and sum sales
const getPotentialItems = (
  aggregations: SalesPerformanceAggregation[],
  groupByKey: 'index' | 'subIndex' | 'garmentGroup' | 'departmentNumber',
  groupByDescription: 'indexDescription' | 'subIndexDescription' | 'garmentGroup' | 'departmentName',
): PotentialItem[] => {
  const groupKeyValueToAggregationsMap: Record<string, SalesPerformanceAggregation[]> = {};

  for (const aggregation of aggregations) {
    const groupKeyValue = String(aggregation[groupByKey] ?? '');
    if (!groupKeyValueToAggregationsMap[groupKeyValue]) groupKeyValueToAggregationsMap[groupKeyValue] = [];
    groupKeyValueToAggregationsMap[groupKeyValue].push(aggregation);
  }

  const potentialItems: PotentialItem[] = [];

  for (const groupKeyValue in groupKeyValueToAggregationsMap) {
    const items = groupKeyValueToAggregationsMap[groupKeyValue];
    const description = items[0][groupByDescription];
    const totalSales = sumRows(items);

    const idxLY = getIndex(totalSales.salesNet, totalSales.salesNetLastYear);
    const idxArea = getIndex(totalSales.salesNetArea, totalSales.salesNetAreaLastYear, false);
    const potentialGain = totalSales.salesNetLastYear * (idxArea / 100) - totalSales.salesNet;

    potentialItems.push({
      id: groupKeyValue,
      description,
      vsLY: idxLY,
      idxArea: Math.round(idxArea),
      potentialGain,
    });
  }

  return potentialItems;
};

export const getTimePeriodToPotentialDataMap = (
  response: StoreSalesPerformanceResponse,
  filterIndex?: string,
): TimePeriodToPotentialDataMap => {
  const timePeriodToPotentialDataMap = {} as TimePeriodToPotentialDataMap;

  salesPerformancePeriods.forEach((period) => {
    let list = response[period].departmentDetails ?? [];

    if (filterIndex) {
      list = list.filter((d) => d.index === filterIndex);
    }

    const getTopPotentialItems = (arr: PotentialItem[]) => {
      const newArr = [...arr];
      const sortedArr = newArr.sort((a, b) => b.potentialGain - a.potentialGain).slice(0, 3);
      return sortedArr;
    };

    timePeriodToPotentialDataMap[period] = {
      index: getTopPotentialItems(getPotentialItems(list, 'index', 'indexDescription')),
      subIndex: getTopPotentialItems(getPotentialItems(list, 'subIndex', 'subIndexDescription')),
      garmentGroup: getTopPotentialItems(getPotentialItems(list, 'garmentGroup', 'garmentGroup')),
      department: getTopPotentialItems(getPotentialItems(list, 'departmentNumber', 'departmentName')),
    };
  });

  return timePeriodToPotentialDataMap;
};
